import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import Card from '../common/Card';
import { getCssVar } from '../../utils/cssVar';
import './SeasonalityChart.css';

const MONTHS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jui', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const STACK_COLOR_VARS = Array.from({ length: 15 }, (_, i) => `--chart-stack-${i + 1}`);

const MAX_KEYWORDS = 15;
const MAX_CATEGORIES = 20;
const MONTHS_TO_SHOW = 24;

const formatVolume = (value) => {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return value.toString();
};

const CustomTooltip = ({ active, payload, label, coordinate, labelSuffix = '' }) => {
  if (!active || !payload || payload.length === 0 || !coordinate) return null;
  const sorted = [...payload].filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
  const total = sorted.reduce((sum, p) => sum + p.value, 0);
  
  const tooltipContent = (
    <div className="seasonality-tooltip">
      <p className="seasonality-tooltip-label">{label}</p>
      <p className="seasonality-tooltip-total">{total.toLocaleString('fr-FR')} recherches</p>
      <div className="seasonality-tooltip-items">
        {sorted.map((entry) => (
          <div key={entry.dataKey} className="seasonality-tooltip-item">
            <span className="seasonality-tooltip-dot" style={{ background: entry.fill }} />
            <span className="seasonality-tooltip-kw">{entry.dataKey}{labelSuffix}</span>
            <span className="seasonality-tooltip-val">{entry.value.toLocaleString('fr-FR')}</span>
          </div>
        ))}
      </div>
    </div>
  );

  return createPortal(
    <div 
      className="seasonality-tooltip-portal"
      style={{ left: coordinate.x, top: coordinate.y + 150 }}
    >
      {tooltipContent}
    </div>,
    document.body
  );
};

const SeasonalityChart = ({ keywords, categories = [] }) => {
  const [selectedItems, setSelectedItems] = useState(null);
  const [extraKeywords, setExtraKeywords] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [viewMode, setViewMode] = useState('category');
  const searchRef = useRef(null);

  const hasCategories = useMemo(() => {
    if (!categories || categories.length === 0) return false;
    return keywords?.some(kw => kw.category) ?? false;
  }, [keywords, categories]);

  const categoryMode = hasCategories && viewMode === 'category';

  const handleToggleMode = useCallback((mode) => {
    setViewMode(mode);
    setSelectedItems(null);
    setExtraKeywords([]);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const topCategories = useMemo(() => {
    if (!hasCategories || !keywords || keywords.length === 0) return [];
    const categoryVolumes = {};
    keywords.forEach((kw) => {
      if (!kw.category || !kw.historicalMetrics?.length) return;
      const cat = kw.category;
      if (!categoryVolumes[cat]) categoryVolumes[cat] = 0;
      categoryVolumes[cat] += kw.historicalMetrics.reduce((sum, m) => sum + m.monthlySearches, 0);
    });
    return Object.entries(categoryVolumes)
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_CATEGORIES)
      .map(([cat]) => cat);
  }, [hasCategories, keywords]);

  const topKeywords = useMemo(() => {
    if (!keywords || keywords.length === 0) return [];
    return keywords
      .filter((kw) => kw.historicalMetrics?.length > 0)
      .map((kw) => ({
        keyword: kw.keyword,
        total: kw.historicalMetrics.reduce((sum, m) => sum + m.monthlySearches, 0),
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, MAX_KEYWORDS)
      .map((kw) => kw.keyword);
  }, [keywords]);

  const allStudyKeywordNames = useMemo(
    () => keywords?.map((kw) => kw.keyword) ?? [],
    [keywords]
  );

  const chartItems = useMemo(() => {
    if (categoryMode) {
      return topCategories;
    }
    return [...new Set([...topKeywords, ...extraKeywords])];
  }, [categoryMode, topCategories, topKeywords, extraKeywords]);

  const stackColors = useMemo(() => STACK_COLOR_VARS.map(getCssVar), []);
  const inactiveLegendColor = useMemo(() => getCssVar('--color-gray-300'), []);
  const gridStroke = useMemo(() => getCssVar('--color-gray-100'), []);
  const tickFill = useMemo(() => getCssVar('--color-gray-400'), []);
  const searchIconStroke = useMemo(() => getCssVar('--color-gray-400'), []);
  const suggestionPlusStroke = useMemo(() => getCssVar('--color-purple-accent'), []);

  const colorMap = useMemo(() => {
    const colors = {};
    chartItems.forEach((item, i) => {
      colors[item] = stackColors[i % stackColors.length];
    });
    return colors;
  }, [chartItems, stackColors]);

  const allMonths = useMemo(() => {
    if (!keywords || chartItems.length === 0) return [];
    
    const monthMap = new Map();
    
    if (categoryMode) {
      keywords.forEach(({ category, historicalMetrics }) => {
        if (!category || !chartItems.includes(category) || !historicalMetrics) return;
        historicalMetrics.forEach(({ year, month, monthlySearches }) => {
          const key = `${year}-${String(month).padStart(2, '0')}`;
          if (!monthMap.has(key)) {
            monthMap.set(key, {
              _sortKey: key,
              label: `${MONTHS_FR[parseInt(month, 10) - 1]} ${String(year).slice(2)}`,
            });
          }
          const entry = monthMap.get(key);
          entry[category] = (entry[category] || 0) + monthlySearches;
        });
      });
    } else {
      const kwData = keywords.filter((kw) => chartItems.includes(kw.keyword));
      kwData.forEach(({ keyword, historicalMetrics }) => {
        if (!historicalMetrics) return;
        historicalMetrics.forEach(({ year, month, monthlySearches }) => {
          const key = `${year}-${String(month).padStart(2, '0')}`;
          if (!monthMap.has(key)) {
            monthMap.set(key, {
              _sortKey: key,
              label: `${MONTHS_FR[parseInt(month, 10) - 1]} ${String(year).slice(2)}`,
            });
          }
          const entry = monthMap.get(key);
          entry[keyword] = (entry[keyword] || 0) + monthlySearches;
        });
      });
    }
    
    return Array.from(monthMap.values())
      .sort((a, b) => a._sortKey.localeCompare(b._sortKey))
      .slice(-MONTHS_TO_SHOW);
  }, [keywords, chartItems, categoryMode]);

  const suggestions = useMemo(() => {
    if (categoryMode || !searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return allStudyKeywordNames
      .filter((kw) => !chartItems.includes(kw) && kw.toLowerCase().includes(q))
      .slice(0, 8);
  }, [categoryMode, searchQuery, allStudyKeywordNames, chartItems]);

  const activeItems = selectedItems ?? chartItems;

  const seasonalityStats = useMemo(() => {
    if (!allMonths || allMonths.length === 0 || activeItems.length === 0) return null;
    
    const getMonthTotal = (monthData) => {
      return activeItems.reduce((sum, item) => sum + (monthData[item] || 0), 0);
    };
    
    const monthlyTotals = allMonths.map(m => getMonthTotal(m));
    const monthCount = monthlyTotals.length;
    const totalVolume = monthlyTotals.reduce((sum, v) => sum + v, 0);
    const averageVolume = Math.round(totalVolume / monthCount);
    
    let evolution24m = null;
    if (monthCount >= 24) {
      const recentMonths = monthlyTotals.slice(-12);
      const previousMonths = monthlyTotals.slice(0, 12);
      const recentTotal = recentMonths.reduce((s, v) => s + v, 0);
      const previousTotal = previousMonths.reduce((s, v) => s + v, 0);
      evolution24m = previousTotal > 0 ? Math.round((recentTotal - previousTotal) / previousTotal * 100) : 0;
    }
    
    let evolution6m = null;
    if (monthCount >= 12) {
      const months6Recent = monthlyTotals.slice(-6).reduce((s, v) => s + v, 0);
      const months6Previous = monthlyTotals.slice(-12, -6).reduce((s, v) => s + v, 0);
      evolution6m = months6Previous > 0 ? Math.round((months6Recent - months6Previous) / months6Previous * 100) : 0;
    }
    
    return { averageVolume, evolution24m, evolution6m, monthCount };
  }, [allMonths, activeItems]);

  const addKeyword = useCallback((kw) => {
    setExtraKeywords((prev) => (prev.includes(kw) ? prev : [...prev, kw]));
    setSelectedItems((prev) => (prev ? [...prev, kw] : null));
    setSearchQuery('');
    setSearchOpen(false);
  }, []);

  const removeExtraKeyword = useCallback((kw, e) => {
    e.stopPropagation();
    setExtraKeywords((prev) => prev.filter((k) => k !== kw));
    setSelectedItems((prev) => (prev ? prev.filter((k) => k !== kw) : null));
  }, []);

  const toggleItem = useCallback((item) => {
    setSelectedItems((prev) => {
      const current = prev ?? chartItems;
      if (current.includes(item)) {
        const next = current.filter((k) => k !== item);
        return next.length === 0 ? current : next;
      }
      return [...current, item];
    });
  }, [chartItems]);

  const toggleAll = useCallback(() => {
    if (chartItems.length === 0) return;
    setSelectedItems((prev) => {
      const current = prev ?? chartItems;
      return current.length === chartItems.length ? [chartItems[0]] : null;
    });
  }, [chartItems]);

  if (chartItems.length === 0) return null;

  const allSelected = activeItems.length === chartItems.length;
  const itemLabel = categoryMode ? 'catégories' : 'mots-clés';

  return (
    <Card className="seasonality-chart">
      <div className="seasonality-main-wrapper">
        <div className="seasonality-left">
          <div className="seasonality-header">
            <div>
              <h3 className="seasonality-title">Vue saisonnalité</h3>
              <p className="seasonality-subtitle">
                Volume de recherche sur 24 mois · {activeItems.length}/{chartItems.length} {itemLabel}
              </p>
            </div>
            {hasCategories && (
              <div className="seasonality-toggle">
                <button
                  className={`seasonality-toggle-btn ${viewMode === 'category' ? 'active' : ''}`}
                  onClick={() => handleToggleMode('category')}
                >
                  Par catégorie
                </button>
                <button
                  className={`seasonality-toggle-btn ${viewMode === 'keyword' ? 'active' : ''}`}
                  onClick={() => handleToggleMode('keyword')}
                >
                  Par mot-clé
                </button>
              </div>
            )}
          </div>
          {seasonalityStats && (
            <div className="seasonality-metrics">
              <div className="seasonality-metric">
                <span className="seasonality-metric-label">Volume moyen ({seasonalityStats.monthCount} mois)</span>
                <span className="seasonality-metric-value">{seasonalityStats.averageVolume.toLocaleString('fr-FR')}</span>
              </div>
              {seasonalityStats.evolution24m !== null && (
                <div className="seasonality-metric">
                  <span className="seasonality-metric-label">Évolution 24 mois</span>
                  <span className={`seasonality-metric-value ${seasonalityStats.evolution24m >= 0 ? 'positive' : 'negative'}`}>
                    {seasonalityStats.evolution24m >= 0 ? '+' : ''}{seasonalityStats.evolution24m} %
                  </span>
                </div>
              )}
              {seasonalityStats.evolution6m !== null && (
                <div className="seasonality-metric">
                  <span className="seasonality-metric-label">Évolution 6 mois</span>
                  <span className={`seasonality-metric-value ${seasonalityStats.evolution6m >= 0 ? 'positive' : 'negative'}`}>
                    {seasonalityStats.evolution6m >= 0 ? '+' : ''}{seasonalityStats.evolution6m} %
                  </span>
                </div>
              )}
            </div>
          )}
          <div className="seasonality-body">
            <ResponsiveContainer width="100%" height={280}>
          <BarChart data={allMonths} margin={{ top: 8, right: 8, left: 0, bottom: 40 }} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 9, fill: tickFill, dy: 8 }}
              axisLine={false}
              tickLine={false}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              tickFormatter={formatVolume}
              tick={{ fontSize: 10, fill: tickFill }}
              axisLine={false}
              tickLine={false}
              width={36}
            />
            <Tooltip 
              content={<CustomTooltip />} 
              cursor={{ fill: 'rgba(99,102,241,0.06)' }} 
            />
            {chartItems.filter((item) => activeItems.includes(item)).map((item) => (
              <Bar key={item} dataKey={item} stackId="a" fill={colorMap[item]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
          </div>
        </div>
        <div className="seasonality-legend">
          {!categoryMode && (
            <div className="seasonality-search-wrapper" ref={searchRef}>
              <div className="seasonality-search-input-row">
                <svg className="seasonality-search-icon" viewBox="0 0 20 20" fill="none">
                  <circle cx="8.5" cy="8.5" r="5.5" stroke={searchIconStroke} strokeWidth="1.5" />
                  <path d="M13 13l3 3" stroke={searchIconStroke} strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <input
                  type="text"
                  className="seasonality-search-input"
                  placeholder="Rechercher un mot-clé..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                  onFocus={() => setSearchOpen(true)}
                />
                {searchQuery && (
                  <button className="seasonality-search-clear" onClick={() => { setSearchQuery(''); setSearchOpen(false); }}>✕</button>
                )}
              </div>
              {searchOpen && suggestions.length > 0 && (
                <div className="seasonality-suggestions">
                  {suggestions.map((kw) => (
                    <div
                      key={kw}
                      className="seasonality-suggestion-item"
                      onMouseDown={() => addKeyword(kw)}
                    >
                      <svg viewBox="0 0 16 16" fill="none" className="seasonality-suggestion-plus">
                        <path d="M8 3v10M3 8h10" stroke={suggestionPlusStroke} strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      {kw}
                    </div>
                  ))}
                </div>
              )}
              {searchOpen && searchQuery.trim() && suggestions.length === 0 && (
                <div className="seasonality-suggestions">
                  <div className="seasonality-suggestion-empty">Aucun résultat</div>
                </div>
              )}
            </div>
          )}
          <button className="seasonality-select-all" onClick={toggleAll}>
            {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
          </button>
          <div className="seasonality-legend-items">
            {chartItems.map((item) => {
              const active = activeItems.includes(item);
              const isExtra = !categoryMode && extraKeywords.includes(item);
              return (
                <div
                  key={item}
                  className={`seasonality-legend-item ${active ? 'active' : 'inactive'}`}
                  onClick={() => toggleItem(item)}
                  role="checkbox"
                  aria-checked={active}
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && toggleItem(item)}
                >
                  <span
                    className="seasonality-legend-dot"
                    style={{ background: active ? colorMap[item] : inactiveLegendColor }}
                  />
                  <span className="seasonality-legend-label">{item}</span>
                  {isExtra && (
                    <button
                      className="seasonality-legend-remove"
                      onClick={(e) => removeExtraKeyword(item, e)}
                      title="Retirer du graphique"
                      aria-label={`Retirer ${item}`}
                    >✕</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SeasonalityChart;
